import { ElementType, useMemo } from 'react';
import DataAlertSharpIcon from '@gamma/ui/icons/DataAlertSharpIcon';
import E911EmergencySharpIcon from '@gamma/ui/icons/E911EmergencySharpIcon';
import HelpSharpIcon from '@gamma/ui/icons/HelpSharpIcon';
import { i18n } from '@investigator/shared/localization';
import { AIInsightVerdict } from '../types';

export enum VerdictLabelKey {
  likelyMalicious = 'likelyMalicious',
  likelyBenign = 'likelyBenign',
  suspicious = 'suspicious',
  undetermined = 'undetermined',
}

interface VerdictConfig {
  bgColor: string;
  icon: ElementType;
  labelKey: VerdictLabelKey;
}

/**
 * Configuration map for verdict badge styling.
 * Add new verdicts here to extend the badge behavior.
 */
const VERDICT_CONFIG: Record<AIInsightVerdict, VerdictConfig> = {
  [AIInsightVerdict.LIKELY_MALICIOUS]: {
    bgColor: 'red.800',
    icon: E911EmergencySharpIcon,
    labelKey: VerdictLabelKey.likelyMalicious,
  },
  [AIInsightVerdict.LIKELY_BENIGN]: {
    bgColor: 'gray.800',
    icon: HelpSharpIcon,
    labelKey: VerdictLabelKey.likelyBenign,
  },
  [AIInsightVerdict.SUSPICIOUS]: {
    bgColor: 'orange.800',
    icon: DataAlertSharpIcon,
    labelKey: VerdictLabelKey.suspicious,
  },
  [AIInsightVerdict.UNDETERMINED]: {
    bgColor: 'gray.800',
    icon: HelpSharpIcon,
    labelKey: VerdictLabelKey.undetermined,
  },
};

/** Default config used when verdict is not found in VERDICT_CONFIG */
const DEFAULT_VERDICT_CONFIG: VerdictConfig = {
  bgColor: 'gray.800',
  icon: HelpSharpIcon,
  labelKey: VerdictLabelKey.likelyBenign,
};

export interface AiVerdictBadgeProps {
  bgColor: string;
  label: string;
  icon: ElementType;
  borderColor?: string;
}

/**
 * Hook that returns the styling props for an AI verdict badge.
 * Uses a config-driven approach for easy extensibility.
 */
export const useAiVerdictBadge = (
  verdict: AIInsightVerdict,
): AiVerdictBadgeProps => {
  return useMemo(() => {
    const verdictCopy = i18n.pages.entities.entityAnalysisModal.verdicts;
    const config = VERDICT_CONFIG[verdict] ?? DEFAULT_VERDICT_CONFIG;

    return {
      bgColor: config.bgColor,
      label: verdictCopy[config.labelKey],
      icon: config.icon,
      borderColor: 'whiteAlpha.200', // All badges have border per design
    };
  }, [verdict]);
};
